import { useEffect, useState } from 'react';
import { Table, Space, Tag, Input, Select, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchUsers, updateUser } from '../store/slices/usersSlice';

const { Option } = Select;

export function UsersPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { users, isLoading, total, currentPage, pageSize } = useSelector((state: RootState) => state.users);
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Combined effect with debounce for search/filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(fetchUsers({ 
        page: currentPage, 
        pageSize, 
        role: roleFilter,
        search: searchTerm 
      }));
    }, searchTerm || roleFilter ? 500 : 0); // Immediate for pagination, debounced for search

    return () => clearTimeout(timeoutId);
  }, [dispatch, currentPage, pageSize, roleFilter, searchTerm]);

  const handleRoleChange = async (id: string, role: string) => {
    try {
      await dispatch(updateUser({ id, data: { role: role as 'USER' | 'ADMIN' | 'CONTROLLER' } })).unwrap();
      message.success('Rôle mis à jour');
    } catch (error) {
      message.error('Erreur lors de la mise à jour');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'red';
      case 'CONTROLLER':
        return 'blue';
      case 'USER':
        return 'green';
      default:
        return 'default';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrateur';
      case 'CONTROLLER':
        return 'Contrôleur';
      case 'USER':
        return 'Utilisateur';
      default:
        return role;
    }
  };

  const columns = [
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: 'Locale',
      dataIndex: 'locale',
      key: 'locale',
    },
    {
      title: 'Solde',
      dataIndex: ['wallet', 'balance'],
      key: 'balance',
      render: (_: any, record: any) => {
        const balance = record?.wallet?.balance;
        return balance !== undefined && balance !== null ? `${(balance / 1000).toFixed(3)} TND` : '0.000 TND';
      },
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('fr-TN'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Select
            value={record.role}
            onChange={(role) => handleRoleChange(record.id, role)}
            size="small"
            className="w-32 sm:w-40"
          >
            <Option value="USER">Utilisateur</Option>
            <Option value="CONTROLLER">Contrôleur</Option>
            <Option value="ADMIN">Administrateur</Option>
          </Select>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">Utilisateurs</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <Input
          placeholder="Rechercher par téléphone ou email"
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:max-w-md"
        />
        <Select
          placeholder="Filtrer par rôle"
          value={roleFilter}
          onChange={setRoleFilter}
          allowClear
          className="w-full sm:w-48"
        >
          <Option value="USER">Utilisateur</Option>
          <Option value="CONTROLLER">Contrôleur</Option>
          <Option value="ADMIN">Administrateur</Option>
        </Select>
      </div>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={users}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              dispatch({ type: 'users/setCurrentPage', payload: page });
              dispatch({ type: 'users/setPageSize', payload: size });
            },
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total}`,
          }}
        />
      </div>
    </div>
  );
}
